import { useMutation } from "@apollo/client/react";
import { Box, IconicButton, TruncatedText } from "@nulogy/components";
import { GridBox4, StyledInput, StyledText } from "components/common/EditableListStyledComponents";
import FieldRow from "components/common/FieldRow";
import Form from "components/controls/Form";
import { formatTeardownTimeInputInHours } from "components/helpers/format";
import { UpdateTeardownTime } from "hooks/apollo/teardownTime/teardownTime.gql";
import React, { useState } from "react";
import DateTime from "utils/DateTime";

const ShowTeardownTime = ({ label, formattedDuration, setEditMode, onDelete, id }) => (
  <>
    <StyledText data-testid="teardown-time-label">
      <TruncatedText fullWidth>{label}</TruncatedText>
    </StyledText>
    <StyledText data-testid="teardown-time-duration">
      <TruncatedText fullWidth>{formattedDuration}</TruncatedText>
    </StyledText>
    <IconicButton
      className="spec-edit-teardown-time-icon"
      data-testid="edit-teardown-time-button"
      icon="edit"
      onClick={() => setEditMode(true)}
      type="button"
    >
      Edit
    </IconicButton>
    <IconicButton
      className="spec-delete-teardown-time-icon"
      data-testid="delete-teardown-time-button"
      icon="delete"
      onClick={() => onDelete(id)}
      type="button"
    >
      Delete
    </IconicButton>
  </>
);

const EditTeardownTime = ({ disableSave, onReset, handleCancel }) => (
  <>
    <StyledInput className="spec-edit-teardown-time-label-input" field="label" placeholder="Teardown Time" />
    <StyledInput
      className="spec-edit-teardown-time-duration-input"
      field="duration"
      step="any"
      style={{ textAlign: "left" }}
      type="number"
    />
    <Box paddingTop="x1">
      <IconicButton className="spec-save-teardown-time-icon" disabled={disableSave} icon="check" type="submit">
        Save
      </IconicButton>
    </Box>
    <Box paddingTop="x1">
      <IconicButton icon="close" onClick={() => handleCancel(onReset)} type="button">
        Cancel
      </IconicButton>
    </Box>
  </>
);

export default function TeardownTime({ teardownTime, onDelete }) {
  const [editMode, setEditMode] = useState(false);
  const [updateTeardownTime] = useMutation(UpdateTeardownTime, { onCompleted: () => setEditMode(false) });

  const { id, label, duration } = teardownTime;

  const handleUpdate = (updatedTeardownTime) =>
    updateTeardownTime({
      variables: {
        duration: DateTime.hoursToSeconds(updatedTeardownTime.duration),
        id: updatedTeardownTime.id,
        label: updatedTeardownTime.label,
      },
    });

  const handleCancel = (onReset) => {
    onReset();
    setEditMode(false);
  };

  const formattedDuration = formatTeardownTimeInputInHours(duration);

  return (
    <>
      {!editMode && (
        <GridBox4 data-testid="teardown-time">
          <ShowTeardownTime
            formattedDuration={formattedDuration}
            id={id}
            label={label}
            onDelete={onDelete}
            setEditMode={setEditMode}
          />
        </GridBox4>
      )}
      {editMode && (
        <Form
          id={`editTeardownTime-${id}`}
          initialValues={{ duration: formattedDuration, id, label }}
          onSave={handleUpdate}
        >
          {({ dirty, handleReset }) => (
            <FieldRow marginBottom="none">
              <GridBox4>
                <EditTeardownTime disableSave={!dirty} handleCancel={handleCancel} onReset={handleReset} />
              </GridBox4>
            </FieldRow>
          )}
        </Form>
      )}
    </>
  );
}
